import { type ApiContainerAction } from '@Model';
import { useCallback, useEffect, useRef, useState } from 'react';
import { lineState } from './lineConfirmation';

export interface ContainerLineFocus {
    focusActionId: string | undefined;
    focusNextPlanned: () => void;
}

/** The row whose next empty field opens: the one just planned, or the first one without a number. */
// @ai-generated 2026-10-15T07:18:24+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
export function useContainerLineFocus(
    containerActions: readonly ApiContainerAction[],
    completing: boolean,
    isPlanning: boolean,
    isIndividual: (action: ApiContainerAction) => boolean,
): ContainerLineFocus {
    const [focusActionId, setFocusActionId] = useState<string>();
    const idsBeforePlanning = useRef<ReadonlySet<string>>(undefined);
    const wasCompleting = useRef(completing);

    useEffect(() => {
        const before = idsBeforePlanning.current;
        if (!before || isPlanning) {
            return;
        }

        idsBeforePlanning.current = undefined;
        const planned = containerActions.find((action) => !before.has(action.id));
        if (planned) {
            setFocusActionId(planned.id);
        }
    }, [containerActions, isPlanning]);

    useEffect(() => {
        if (completing && !wasCompleting.current) {
            const withoutNumber = containerActions.find(
                (action) =>
                    lineState(action.booking_status) === 'open' &&
                    !action.container.foreign_key &&
                    isIndividual(action),
            );
            setFocusActionId(withoutNumber?.id);
        }
        wasCompleting.current = completing;
    }, [completing, containerActions, isIndividual]);

    const focusNextPlanned = useCallback(() => {
        idsBeforePlanning.current = new Set(containerActions.map((action) => action.id));
    }, [containerActions]);

    return { focusActionId, focusNextPlanned };
}
